import React, { useEffect } from 'react';
import MultiSelectDropdown from '../../ui/MultiSelectDropdown';
import SearchableDropdown from '../../ui/SearchableDropdown';
import { UseMapVisualizationReturn, VisualizationTypeOption } from '../../../hooks/useMapVisualization';
import { StatType } from '../../../types/database';
import AdvancedVisualizationControls from './AdvancedVisualizationControls';
import { TeamShapeConfig, ShapeType } from '../../../hooks/useTeamShape';

interface VisualizationControlsProps {
  mapVisualization: UseMapVisualizationReturn;
  statTypes: StatType[];
  disableNormalizeToggle?: boolean; // Disable normalize toggle (e.g., for Tactics tab where it's always enabled)
  disableColorBy?: boolean; // Disable color by selector (e.g., for Tactics tab where it's always "team")
  hideVisualizationControls?: boolean; // Hide visualization type, point selection, line scale, and advanced controls (e.g., for Tactics tab)
  // Team Shape specific props
  teamShapeConfig?: TeamShapeConfig;
  onTeamShapeConfigChange?: (config: TeamShapeConfig) => void;
  teamShapeLoading?: boolean;
  teamShapePointCount?: number;
  homeTeamName?: string;
  awayTeamName?: string;
  showBothTeams?: boolean; // Whether both teams are selected in single view mode
}

/**
 * VisualizationControls - Controls for visualization type, compare mode, normalization, etc.
 * Extracted from MapsTab to be reusable across components
 */
const VisualizationControls: React.FC<VisualizationControlsProps> = ({
  mapVisualization,
  statTypes,
  disableNormalizeToggle = false,
  disableColorBy = false,
  hideVisualizationControls = false,
  teamShapeConfig,
  onTeamShapeConfigChange,
  teamShapeLoading = false,
  teamShapePointCount = 0,
  homeTeamName = 'Home Team',
  awayTeamName = 'Away Team',
  showBothTeams = false,
}) => {
  const {
    compareMode,
    setCompareMode,
    normalizeToFirstHalf,
    setNormalizeToFirstHalf,
    colorBy,
    setColorBy,
    whiteBackground,
    setWhiteBackground,
    visualizationType,
    setVisualizationType,
    pointSelection,
    setPointSelection,
    trajectoryDensity,
    setTrajectoryDensity,
    filters,
    statTypeVisualizationMap,
    setStatTypeVisualizationMap,
  } = mapVisualization;

  // Check if team_shape is selected
  const isTeamShapeSelected = visualizationType.includes('team_shape');

  // Auto-set normalize to first half and color by team when team_shape is selected
  useEffect(() => {
    if (isTeamShapeSelected) {
      if (!normalizeToFirstHalf) {
        setNormalizeToFirstHalf(true);
      }
      if (colorBy !== 'team') {
        setColorBy('team');
      }
    }
  }, [isTeamShapeSelected, normalizeToFirstHalf, setNormalizeToFirstHalf, colorBy, setColorBy]);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 overflow-visible">
      <div className="space-y-4">
        {/* Compare Mode and Direction Settings */}
        <div className="flex flex-wrap gap-4 items-center pb-3 border-b border-gray-200">
          {/* Compare Mode Toggle */}
          <div className="flex items-center space-x-3">
            <label className="text-sm font-medium text-gray-700">Compare Mode:</label>
            <button
              type="button"
              onClick={() => setCompareMode(!compareMode)}
              className={`
                relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                ${compareMode ? 'bg-blue-600' : 'bg-gray-200'}
              `}
              role="switch"
              aria-checked={compareMode}
            >
              <span
                className={`
                  pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out
                  ${compareMode ? 'translate-x-5' : 'translate-x-0'}
                `}
              />
            </button>
            <span className="text-sm text-gray-700">
              {compareMode ? 'Side by Side' : 'Single View'}
            </span>
          </div>

          {/* Normalize to First Half Toggle */}
          <div className="flex items-center space-x-3">
            <label className="text-sm font-medium text-gray-700">Direction of 2nd halves:</label>
            <button
              type="button"
              onClick={() => !disableNormalizeToggle && !isTeamShapeSelected && setNormalizeToFirstHalf(!normalizeToFirstHalf)}
              disabled={disableNormalizeToggle || isTeamShapeSelected}
              className={`
                relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                ${normalizeToFirstHalf ? 'bg-blue-600' : 'bg-gray-200'}
                ${(disableNormalizeToggle || isTeamShapeSelected) ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
              `}
              role="switch"
              aria-checked={normalizeToFirstHalf}
              aria-disabled={disableNormalizeToggle || isTeamShapeSelected}
            >
              <span
                className={`
                  pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out
                  ${normalizeToFirstHalf ? 'translate-x-5' : 'translate-x-0'}
                `}
              />
            </button>
            <span className={`text-sm ${(disableNormalizeToggle || isTeamShapeSelected) ? 'text-gray-500' : 'text-gray-700'}`}>
              {normalizeToFirstHalf ? 'Normalize to First Half' : 'Natural Direction'}
              {(disableNormalizeToggle || isTeamShapeSelected) && ' (Always enabled for team shape)'}
            </span>
          </div>

          {/* Color By Selector */}
          {!disableColorBy && !isTeamShapeSelected && (
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700">Color by:</label>
              <SearchableDropdown
                value={colorBy}
                onChange={(value) => setColorBy(value as 'team' | 'stat_type')}
                options={[
                  { id: 'team', name: 'Team' },
                  { id: 'stat_type', name: 'Stat Type' },
                ]}
                placeholder="Select color by..."
                className="min-w-[150px]"
                showClearIcon={false}
              />
            </div>
          )}
          {/* Color By Display (when team_shape is selected) */}
          {!disableColorBy && isTeamShapeSelected && (
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-500">Color by:</label>
              <span className="text-sm text-gray-500">Team (always enabled for team shape)</span>
            </div>
          )}

          {/* White Background Toggle */}
          <div className="flex items-center space-x-3">
            <label className="text-sm font-medium text-gray-700">White Background:</label>
            <button
              type="button"
              onClick={() => setWhiteBackground(!whiteBackground)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                whiteBackground ? 'bg-blue-600' : 'bg-gray-300'
              }`}
              role="switch"
              aria-checked={whiteBackground}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  whiteBackground ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        </div>

        {/* Per-Stat-Type Visualization Configuration */}
        {!hideVisualizationControls && filters.stat_type_id && filters.stat_type_id.length > 0 && (
          <div className="border-b border-gray-200 pb-4 overflow-visible">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Visualization Type per Stat Type:
            </label>
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 overflow-visible">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 relative" style={{ zIndex: 1 }}>
                {filters.stat_type_id.map((statTypeId) => {
                  const statType = statTypes.find((st) => st.id === statTypeId);
                  const statTypeName = statType?.display_name || statType?.short_description || statType?.stat_key || 'Unknown';
                  
                  return (
                    <div 
                      key={statTypeId} 
                      className="relative bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow p-3 overflow-visible"
                    >
                      <div className="flex flex-col space-y-2.5">
                        {/* Stat Type Name */}
                        <label className="text-xs font-semibold text-gray-800 block truncate leading-tight" title={statTypeName}>
                          {statTypeName}
                        </label>
                        {/* Visualization Type Selector - Multi-select with checkboxes */}
                        <MultiSelectDropdown
                          selectedIds={statTypeVisualizationMap[statTypeId] || ['scatter']}
                          onChange={(ids) => {
                            const selected = ids as VisualizationTypeOption[];
                            setStatTypeVisualizationMap((prev) => ({
                              ...prev,
                              [statTypeId]: selected.length > 0 ? selected : ['scatter'],
                            }));
                          }}
                          options={[
                            { id: 'scatter', name: 'Scatter' },
                            { id: 'trajectory', name: 'Line' },
                            { id: 'heatmap', name: 'Heatmap' },
                            { id: 'grid', name: 'Grid' },
                          ]}
                          placeholder="Select types..."
                          showSearch={false}
                          className="[&_div]:text-xs"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {!hideVisualizationControls && (
          <div className="flex flex-wrap gap-4 items-center">
            {/* Global Visualization Type Selector - Always visible */}
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700">View:</label>
              <MultiSelectDropdown
                selectedIds={visualizationType}
                onChange={(ids) => {
                  const selected = ids as VisualizationTypeOption[];
                  let newVisualizationType: VisualizationTypeOption[];
                  
                  // If team_shape is selected, it must be the only option
                  if (selected.includes('team_shape')) {
                    newVisualizationType = ['team_shape'];
                  } else if (selected.length > 0) {
                    // If other types are selected, remove team_shape if it exists
                    const filtered = selected.filter(id => id !== 'team_shape');
                    newVisualizationType = filtered.length > 0 ? filtered : ['scatter'];
                  } else {
                    newVisualizationType = ['scatter']; // At least one must be selected
                  }
                  
                  setVisualizationType(newVisualizationType);
                  
                  // Apply to all per-stat selectors that haven't been manually modified
                  // We apply to ALL when global is changed (user can override individually after)
                  if (filters.stat_type_id && filters.stat_type_id.length > 0) {
                    const updatedMap: Record<string, VisualizationTypeOption[]> = {};
                    filters.stat_type_id.forEach((statTypeId) => {
                      updatedMap[statTypeId] = newVisualizationType;
                    });
                    setStatTypeVisualizationMap(updatedMap);
                  }
                }}
                options={[
                  { id: 'scatter', name: 'Scatter Plot' },
                  { id: 'trajectory', name: 'Lines' },
                  { id: 'heatmap', name: 'Heatmap' },
                  { id: 'grid', name: 'Grid' },
                  { id: 'team_shape', name: 'Team Shape' },
                ]}
                placeholder="Select visualization types..."
                showSearch={false}
                className="min-w-[150px]"
              />
            </div>

          {/* Point Selection Selector - Show when scatter, grid, or heatmap view is selected */}
          {(() => {
            // If per-stat-type mapping is active, only check per-stat mappings
            if (filters.stat_type_id && filters.stat_type_id.length > 0) {
              return filters.stat_type_id.some(id => {
                const vizTypes = statTypeVisualizationMap[id] || ['scatter'];
                return vizTypes.includes('scatter') || vizTypes.includes('grid') || vizTypes.includes('heatmap');
              });
            }
            // Otherwise, check global visualization type
            return visualizationType.includes('scatter') || visualizationType.includes('grid') || visualizationType.includes('heatmap');
          })() && (
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700">Point Selection:</label>
              <MultiSelectDropdown
                selectedIds={pointSelection}
                onChange={(ids) => {
                  const selected = ids as ('source' | 'destination')[];
                  setPointSelection(selected.length > 0 ? selected : ['source']); // At least one must be selected
                }}
                options={[
                  { id: 'source', name: 'Source' },
                  { id: 'destination', name: 'Destination' },
                ]}
                placeholder="Select point types..."
                showSearch={false}
                className="min-w-[180px]"
              />
            </div>
          )}

          {/* Line Scale Selector - Show when trajectory-related view is selected */}
          {(() => {
            // If per-stat-type mapping is active, only check per-stat mappings
            if (filters.stat_type_id && filters.stat_type_id.length > 0) {
              return filters.stat_type_id.some(id => {
                const vizTypes = statTypeVisualizationMap[id] || ['scatter'];
                return vizTypes.includes('trajectory');
              });
            }
            // Otherwise, check global visualization type
            return visualizationType.includes('trajectory');
          })() && (
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700">Line Scale:</label>
              <SearchableDropdown
                value={trajectoryDensity}
                onChange={(value) => setTrajectoryDensity(value as 'all' | 'medium' | 'low' | 'first_last_only')}
                options={[
                  { id: 'all', name: 'All Points' },
                  { id: 'medium', name: 'Medium Density' },
                  { id: 'low', name: 'Low Density' },
                  { id: 'first_last_only', name: 'Straight Line' },
                ]}
                placeholder="Select line scale..."
                className="min-w-[150px]"
                showClearIcon={false}
              />
            </div>
          )}
          </div>
        )}

        {/* Advanced Visualization Controls (Heatmap & Grid) - Only show when relevant types are selected */}
        {!hideVisualizationControls && !isTeamShapeSelected && (
          <AdvancedVisualizationControls mapVisualization={mapVisualization} />
        )}

        {/* Team Shape Controls - Show when team_shape is selected */}
        {isTeamShapeSelected && teamShapeConfig && onTeamShapeConfigChange && (
          <div className="pt-3 border-t border-gray-200 mt-3 space-y-3">
            {/* Mode Selection Row */}
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">Mode:</label>
                <div className="flex space-x-1">
                  <button
                    onClick={() => onTeamShapeConfigChange({ ...teamShapeConfig, mode: 'simple', playerCount: 11 })}
                    disabled={teamShapeLoading}
                    className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                      teamShapeConfig.mode === 'simple'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    } ${teamShapeLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    Auto
                  </button>
                  <button
                    onClick={() => onTeamShapeConfigChange({ 
                      ...teamShapeConfig, 
                      mode: 'formation', 
                      formation: teamShapeConfig.formation || { defense: 4, midfield: 3, attack: 3 }
                    })}
                    disabled={teamShapeLoading}
                    className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                      teamShapeConfig.mode === 'formation'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    } ${teamShapeLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    Custom
                  </button>
                </div>
              </div>

              {/* Simple Mode Controls - Show immediately after Mode when Auto is selected */}
              {teamShapeConfig.mode === 'simple' && (
                <div className="flex items-center space-x-2">
                  <label className="text-sm font-medium text-gray-700">Players:</label>
                  <input
                    type="range"
                    min="1"
                    max="22"
                    value={teamShapeConfig.playerCount || 11}
                    onChange={(e) => onTeamShapeConfigChange({ ...teamShapeConfig, playerCount: parseInt(e.target.value) })}
                    disabled={teamShapeLoading}
                    className="w-24"
                  />
                  <span className="text-sm text-gray-600 w-8">{teamShapeConfig.playerCount || 11}</span>
                </div>
              )}
            </div>

            {/* Formation Mode Controls - Show immediately after Mode when Custom is selected */}
            {teamShapeConfig.mode === 'formation' && (
              <div className="flex flex-wrap gap-4 items-center">
                {(compareMode || showBothTeams) ? (
                  // Compare Mode or Both Teams Selected: Show separate formations for each team
                  <>
                    <div className="flex items-center space-x-2">
                      <label className="text-sm font-medium text-gray-700">{homeTeamName}:</label>
                      <input
                        type="number"
                        min="0"
                        max="11"
                        value={teamShapeConfig.homeFormation?.defense || teamShapeConfig.formation?.defense || 4}
                        onChange={(e) => onTeamShapeConfigChange({
                          ...teamShapeConfig,
                          homeFormation: {
                            ...(teamShapeConfig.homeFormation || teamShapeConfig.formation || { defense: 4, midfield: 3, attack: 3 }),
                            defense: parseInt(e.target.value) || 0
                          }
                        })}
                        disabled={teamShapeLoading}
                        className="w-12 px-1 py-1 border border-gray-300 rounded text-sm text-center"
                        placeholder="D"
                      />
                      <span className="text-sm text-gray-500">-</span>
                      <input
                        type="number"
                        min="0"
                        max="11"
                        value={teamShapeConfig.homeFormation?.midfield || teamShapeConfig.formation?.midfield || 3}
                        onChange={(e) => onTeamShapeConfigChange({
                          ...teamShapeConfig,
                          homeFormation: {
                            ...(teamShapeConfig.homeFormation || teamShapeConfig.formation || { defense: 4, midfield: 3, attack: 3 }),
                            midfield: parseInt(e.target.value) || 0
                          }
                        })}
                        disabled={teamShapeLoading}
                        className="w-12 px-1 py-1 border border-gray-300 rounded text-sm text-center"
                        placeholder="M"
                      />
                      <span className="text-sm text-gray-500">-</span>
                      <input
                        type="number"
                        min="0"
                        max="11"
                        value={teamShapeConfig.homeFormation?.attack || teamShapeConfig.formation?.attack || 3}
                        onChange={(e) => onTeamShapeConfigChange({
                          ...teamShapeConfig,
                          homeFormation: {
                            ...(teamShapeConfig.homeFormation || teamShapeConfig.formation || { defense: 4, midfield: 3, attack: 3 }),
                            attack: parseInt(e.target.value) || 0
                          }
                        })}
                        disabled={teamShapeLoading}
                        className="w-12 px-1 py-1 border border-gray-300 rounded text-sm text-center"
                        placeholder="A"
                      />
                    </div>
                    <div className="flex items-center space-x-2">
                      <label className="text-sm font-medium text-gray-700">{awayTeamName}:</label>
                      <input
                        type="number"
                        min="0"
                        max="11"
                        value={teamShapeConfig.awayFormation?.defense || teamShapeConfig.formation?.defense || 4}
                        onChange={(e) => onTeamShapeConfigChange({
                          ...teamShapeConfig,
                          awayFormation: {
                            ...(teamShapeConfig.awayFormation || teamShapeConfig.formation || { defense: 4, midfield: 3, attack: 3 }),
                            defense: parseInt(e.target.value) || 0
                          }
                        })}
                        disabled={teamShapeLoading}
                        className="w-12 px-1 py-1 border border-gray-300 rounded text-sm text-center"
                        placeholder="D"
                      />
                      <span className="text-sm text-gray-500">-</span>
                      <input
                        type="number"
                        min="0"
                        max="11"
                        value={teamShapeConfig.awayFormation?.midfield || teamShapeConfig.formation?.midfield || 3}
                        onChange={(e) => onTeamShapeConfigChange({
                          ...teamShapeConfig,
                          awayFormation: {
                            ...(teamShapeConfig.awayFormation || teamShapeConfig.formation || { defense: 4, midfield: 3, attack: 3 }),
                            midfield: parseInt(e.target.value) || 0
                          }
                        })}
                        disabled={teamShapeLoading}
                        className="w-12 px-1 py-1 border border-gray-300 rounded text-sm text-center"
                        placeholder="M"
                      />
                      <span className="text-sm text-gray-500">-</span>
                      <input
                        type="number"
                        min="0"
                        max="11"
                        value={teamShapeConfig.awayFormation?.attack || teamShapeConfig.formation?.attack || 3}
                        onChange={(e) => onTeamShapeConfigChange({
                          ...teamShapeConfig,
                          awayFormation: {
                            ...(teamShapeConfig.awayFormation || teamShapeConfig.formation || { defense: 4, midfield: 3, attack: 3 }),
                            attack: parseInt(e.target.value) || 0
                          }
                        })}
                        disabled={teamShapeLoading}
                        className="w-12 px-1 py-1 border border-gray-300 rounded text-sm text-center"
                        placeholder="A"
                      />
                    </div>
                  </>
                ) : (
                  // Single Team Mode: Show shared formation
                  <div className="flex items-center space-x-2">
                    <label className="text-sm font-medium text-gray-700">Formation:</label>
                    <input
                      type="number"
                      min="0"
                      max="11"
                      value={teamShapeConfig.formation?.defense || 4}
                      onChange={(e) => onTeamShapeConfigChange({
                        ...teamShapeConfig,
                        formation: {
                          ...(teamShapeConfig.formation || { defense: 4, midfield: 3, attack: 3 }),
                          defense: parseInt(e.target.value) || 0
                        }
                      })}
                      disabled={teamShapeLoading}
                      className="w-12 px-1 py-1 border border-gray-300 rounded text-sm text-center"
                      placeholder="D"
                    />
                    <span className="text-sm text-gray-500">-</span>
                    <input
                      type="number"
                      min="0"
                      max="11"
                      value={teamShapeConfig.formation?.midfield || 3}
                      onChange={(e) => onTeamShapeConfigChange({
                        ...teamShapeConfig,
                        formation: {
                          ...(teamShapeConfig.formation || { defense: 4, midfield: 3, attack: 3 }),
                          midfield: parseInt(e.target.value) || 0
                        }
                      })}
                      disabled={teamShapeLoading}
                      className="w-12 px-1 py-1 border border-gray-300 rounded text-sm text-center"
                      placeholder="M"
                    />
                    <span className="text-sm text-gray-500">-</span>
                    <input
                      type="number"
                      min="0"
                      max="11"
                      value={teamShapeConfig.formation?.attack || 3}
                      onChange={(e) => onTeamShapeConfigChange({
                        ...teamShapeConfig,
                        formation: {
                          ...(teamShapeConfig.formation || { defense: 4, midfield: 3, attack: 3 }),
                          attack: parseInt(e.target.value) || 0
                        }
                      })}
                      disabled={teamShapeLoading}
                      className="w-12 px-1 py-1 border border-gray-300 rounded text-sm text-center"
                      placeholder="A"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Shape Type Selection Row */}
            <div className="flex flex-wrap gap-4 items-center">
              {(compareMode || showBothTeams) ? (
              // Compare Mode or Both Teams Selected: Show separate shape type selections for each team
              <>
                <div className="flex items-center space-x-2">
                  <label className="text-sm font-medium text-gray-700">{homeTeamName} Shape:</label>
                  <MultiSelectDropdown
                    selectedIds={teamShapeConfig.homeShapeType || teamShapeConfig.shapeType || []}
                    onChange={(ids) => {
                      const selected = ids as ShapeType[];
                      onTeamShapeConfigChange({
                        ...teamShapeConfig,
                        homeShapeType: selected
                      });
                    }}
                    options={[
                      { id: 'attacking', name: 'Attacking' },
                      { id: 'defensive', name: 'Defensive' },
                    ]}
                    placeholder="Select shape type..."
                    showSearch={false}
                    className="min-w-[120px]"
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <label className="text-sm font-medium text-gray-700">{awayTeamName} Shape:</label>
                  <MultiSelectDropdown
                    selectedIds={teamShapeConfig.awayShapeType || teamShapeConfig.shapeType || []}
                    onChange={(ids) => {
                      const selected = ids as ShapeType[];
                      onTeamShapeConfigChange({
                        ...teamShapeConfig,
                        awayShapeType: selected
                      });
                    }}
                    options={[
                      { id: 'attacking', name: 'Attacking' },
                      { id: 'defensive', name: 'Defensive' },
                    ]}
                    placeholder="Select shape type..."
                    showSearch={false}
                    className="min-w-[120px]"
                  />
                </div>
              </>
            ) : (
              // Single Team Mode: Show shared shape type selection
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">Shape Type:</label>
                <MultiSelectDropdown
                  selectedIds={teamShapeConfig.shapeType || []}
                  onChange={(ids) => {
                    const selected = ids as ShapeType[];
                    onTeamShapeConfigChange({
                      ...teamShapeConfig,
                      shapeType: selected
                    });
                  }}
                  options={[
                    { id: 'attacking', name: 'Attacking' },
                    { id: 'defensive', name: 'Defensive' },
                  ]}
                  placeholder="Select shape type..."
                  showSearch={false}
                  className="min-w-[120px]"
                />
              </div>
              )}

            {/* Networks Toggle */}
            <div className="flex items-center space-x-3">
              <label className="text-sm font-medium text-gray-700">Networks:</label>
              <button
                type="button"
                onClick={() => onTeamShapeConfigChange({ ...teamShapeConfig, showNetworks: !teamShapeConfig.showNetworks })}
                disabled={teamShapeLoading}
                className={`
                  relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                  ${teamShapeConfig.showNetworks ? 'bg-blue-600' : 'bg-gray-200'}
                  ${teamShapeLoading ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
                `}
                role="switch"
                aria-checked={teamShapeConfig.showNetworks}
              >
                <span
                  className={`
                    pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out
                    ${teamShapeConfig.showNetworks ? 'translate-x-5' : 'translate-x-0'}
                  `}
                />
              </button>
              <span className="text-sm text-gray-700">
                {teamShapeConfig.showNetworks ? 'Show Connections' : 'Hide Connections'}
              </span>
            </div>

              {/* Loading indicator */}
              {teamShapeLoading && (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-sm text-gray-600">Calculating...</span>
                </div>
              )}

              {/* Point count */}
              {teamShapePointCount > 0 && (
                <span className="text-xs text-gray-500 ml-auto">
                  {teamShapePointCount} points
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VisualizationControls;

